<!--@prettier-->
<template>
  <Dialog
    id="file-viewer-dialog"
    class="p-dialog-fullscreen"
    :class="{ loading: isLoading }"
    :visible="appState.fileViewerDialog.isVisible"
    :header="appState.fileViewerDialog.fileName"
    @show="onDialogShow()"
    @update:visible="hideDialog()"
  >
    <iframe
      id="file-viewer-iframe"
      :title="appState.fileViewerDialog.fileName"
      :src="iframeSrc"
      allowfullscreen
    ></iframe>
    <Button
      v-if="appState.fileViewerDialog.carousel"
      icon="material-symbols-outlined arrow_back"
      @click="changeDocument(-1)"
      class="p-button-rounded p-button-icon-only p-button-lg p-button-raised carousel-prev"
      :disabled="appState.fileViewerDialog.carousel.activeIndex === 0"
    />
    <Button
      v-if="appState.fileViewerDialog.carousel"
      icon="material-symbols-outlined arrow_forward"
      @click="changeDocument(1)"
      class="p-button-rounded p-button-icon-only p-button-lg p-button-raised carousel-next"
      :disabled="
        appState.fileViewerDialog.carousel.activeIndex ===
        appState.fileViewerDialog.carousel.documents.length - 1
      "
    />
    <template #footer>
      <div class="flex align-items-center">
        <span
          v-if="appState.fileViewerDialog.carousel"
          class="mb-2"
          >{{ appState.fileViewerDialog.carousel.activeIndex + 1 }} of
          {{ appState.fileViewerDialog.carousel.documents.length }}</span
        >
        <span class="ml-auto">
          <Button
            label="Cancel"
            icon="material-symbols-outlined close"
            class="p-button-rounded p-button-text"
            @click="hideDialog()"
          />
          <Button
            label="Download"
            icon="material-symbols-outlined download"
            @click="downloadFile()"
            class="p-button-rounded"
          />
        </span>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
  import { ref, inject } from "vue";
  import getFileFromBlob from "@/utilities/getFileFromBlob.js";
  import { getDocument } from "@/services/coreCommonDataService/document.js";
  import webSafeImageMimeTypes from "@/data/webSafeImageMimeTypes.js";

  const appState = inject("appState");
  const iframeSrc = ref();
  const isLoading = ref(false);

  // when the dialog is shown, get a url to the blob to show in the iframe
  const onDialogShow = () => {
    if (appState.fileViewerDialog.carousel) addArrowKeysEventListener();
    iframeSrc.value = URL.createObjectURL(appState.fileViewerDialog.blob);
  };

  // when hiding the dialog, reset all appState.fileViewerDialog properties
  const hideDialog = () => {
    if (appState.fileViewerDialog.carousel) removeArrowKeysEventListener();
    appState.fileViewerDialog = {
      isVisible: false,
      fileName: "",
      blob: null,
      carousel: null
    };
  };

  // if the carousel is available, this controls the previous and next buttons
  const changeDocument = async (indexChange) => {
    isLoading.value = true;
    appState.fileViewerDialog.carousel.activeIndex += indexChange;
    // if we've already fetched this file, its blob will be saved here. use it.
    // if we haven't already fetched this file, go fetch the blob and save it.
    if (
      appState.fileViewerDialog.carousel.documents[appState.fileViewerDialog.carousel.activeIndex]
        .blob !== null
    ) {
      appState.fileViewerDialog.blob =
        appState.fileViewerDialog.carousel.documents[
          appState.fileViewerDialog.carousel.activeIndex
        ].blob;
    } else {
      appState.fileViewerDialog.blob = await getDocument(
        appState.fileViewerDialog.carousel.documents[appState.fileViewerDialog.carousel.activeIndex]
          .id
      );
      appState.fileViewerDialog.carousel.documents[
        appState.fileViewerDialog.carousel.activeIndex
      ].blob = appState.fileViewerDialog.blob;
    }
    // set the file name for the header of the dialog
    appState.fileViewerDialog.fileName =
      appState.fileViewerDialog.carousel.documents[
        appState.fileViewerDialog.carousel.activeIndex
      ].fileName;
    // check to see if this is either a websafe image or a pdf.
    // if it is one of those, show in iframe
    // if it is not one of those, show a generic "no preview available" message in iframe
    const fileExtension = appState.fileViewerDialog.fileName.split(".").pop();
    const isImage = webSafeImageMimeTypes.includes(appState.fileViewerDialog.blob.type);
    const isPdf =
      ["application/pdf"].includes(appState.fileViewerDialog.blob.type) || fileExtension === "pdf";
    if (fileExtension === "pdf" && appState.fileViewerDialog.blob.type !== "application/pdf")
      appState.fileViewerDialog.blob = new Blob([appState.fileViewerDialog.blob], {
        type: "application/pdf"
      });
    if (isImage || isPdf) {
      iframeSrc.value = URL.createObjectURL(appState.fileViewerDialog.blob);
    } else {
      iframeSrc.value =
        "data:text/html,<html><head></head><body style='margin:0; padding:0;'><div style='height:100%; padding:0 4rem; display:flex; align-items:center; justify-content:center; text-align:center; font-family:monospace; background-color:dimgray; color:white;'>We couldn't generate a preview for this&nbsp;document.<br /><br />Please download the document to view&nbsp;it.</div></body></html>";
    }
    isLoading.value = false;
  };

  // handle arrow keypresses for next/previous carousel functionality
  function keyDownHandler(event) {
    if (
      !["ArrowLeft", "ArrowRight"].includes(event.key) ||
      isLoading.value ||
      appState.fileViewerDialog.carousel === null
    )
      return;
    if (event.code === "ArrowLeft") {
      if (appState.fileViewerDialog.carousel.activeIndex === 0) return;
      changeDocument(-1);
    } else if (event.code === "ArrowRight") {
      if (
        appState.fileViewerDialog.carousel.activeIndex ===
        appState.fileViewerDialog.carousel.documents.length - 1
      )
        return;
      changeDocument(1);
    }
  }
  const addArrowKeysEventListener = () => {
    window.addEventListener("keydown", keyDownHandler);
  };
  const removeArrowKeysEventListener = () => {
    window.removeEventListener("keydown", keyDownHandler);
  };

  // if the user wishes to download the file, allow them to click a "download" button to force download
  const downloadFile = () => {
    getFileFromBlob(appState.fileViewerDialog.blob, appState.fileViewerDialog.fileName, {
      forceDownload: true
    });
  };
</script>

<style lang="scss">
  #file-viewer-dialog {
    .p-dialog-header {
      padding: 1rem;
      @media (min-width: $breakpoint-lg) {
        padding: 2rem;
      }
      .p-dialog-title {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
    .p-dialog-content {
      position: relative;
      flex: 1;
      padding-top: 0;
      padding-bottom: 0;
      #file-viewer-iframe {
        border: 0;
        position: absolute;
        top: 0;
        left: 1rem;
        width: calc(100% - 2rem);
        height: 100%;
        @media (min-width: $breakpoint-lg) {
          top: 0;
          left: 2rem;
          width: calc(100% - 4rem);
          height: 100%;
        }
      }
      .carousel-prev,
      .carousel-next {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
      .carousel-prev {
        left: 2rem;
        @media (min-width: $breakpoint-lg) {
          left: 4rem;
        }
      }
      .carousel-next {
        right: 2rem;
        @media (min-width: $breakpoint-lg) {
          right: 4rem;
        }
      }
    }
    .p-dialog-footer {
      padding: 1rem 1rem 0.5rem 1rem;
      @media (min-width: $breakpoint-lg) {
        padding: 2rem 2rem 1rem;
      }
    }
  }
</style>
